import { useCallback, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { getBusinessToday, getBusinessTimeHHmm } from "@/utils/businessTime";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  LinearProgress,
  Avatar,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Alert,
} from "@mui/material";
import {
  ArrowBack,
  LocationOn,
  Phone,
  Map as MapIcon,
  CheckCircle,
  Cancel,
  ReceiptLong,
} from "@mui/icons-material";
import { deliveryService, DeliveryStatus } from "@/services/delivery.service";
import { LoadingSpinner, PageHeader, Button as AppButton } from "@/components/ui";
import { useToastStore } from "@/store/toastStore";
import { formatCustomerName, getCustomerInitials } from "@/utils/formatCustomerName";
import { toTelHref } from "@/constants/phoneDialCodes";
import {
  ORDER_PAYMENT_SETTLEMENT_OPTIONS,
  type OrderPaymentSettlementCode,
} from "@/constants/order-payment-settlement";

const STATUS_LABELS: Record<DeliveryStatus, string> = {
  PLANNED: "Planifiée",
  IN_PROGRESS: "En cours",
  DELIVERED: "Livrée",
  FAILED: "Échec",
};

const FAILURE_CAUSES = [
  { value: "client_absent", label: "Client absent" },
  { value: "adresse_incorrecte", label: "Adresse incorrecte" },
  { value: "client_refuse", label: "Client refuse la livraison" },
  { value: "produit_endommage", label: "Produit endommagé" },
  { value: "autre", label: "Autre" },
];

function getMapUrl(addr: { address: string; postalCode?: string | null; city?: string | null } | null | undefined): string {
  if (!addr) return "";
  const q = [addr.address, addr.postalCode, addr.city].filter(Boolean).join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

export function DeliveryMyDetailPage(): JSX.Element {
  const { deliveryId } = useParams<{ deliveryId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastStore();

  const today = getBusinessToday();

  const { data: deliveries, isLoading: loadingList } = useQuery({
    queryKey: ["deliveries", "my", today],
    queryFn: () => deliveryService.getAll({ deliveryDate: today }),
  });

  const { data: delivery, isLoading: loadingDetail } = useQuery({
    queryKey: ["delivery", deliveryId],
    queryFn: () => deliveryService.getById(deliveryId!),
    enabled: !!deliveryId,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({
      id,
      status,
      actualDeliveryTime,
      notes,
    }: {
      id: string;
      status: DeliveryStatus;
      actualDeliveryTime?: string;
      notes?: string;
    }) => deliveryService.updateStatus(id, status, actualDeliveryTime, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deliveries", "my"] });
      queryClient.invalidateQueries({ queryKey: ["delivery", deliveryId] });
      success("Statut mis à jour");
    },
    onError: (err: Error) => {
      showError(err.message || "Erreur lors de la mise à jour");
    },
  });

  const [openDelivered, setOpenDelivered] = useState(false);
  const [openFailed, setOpenFailed] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<OrderPaymentSettlementCode>("CB");
  const [deliveredComment, setDeliveredComment] = useState("");
  const [failureCause, setFailureCause] = useState("");
  const [failedComment, setFailedComment] = useState("");

  const sortedDeliveries = useMemo(() => {
    const list = deliveries ?? [];
    return [...list].sort((a, b) => a.tourOrder - b.tourOrder);
  }, [deliveries]);

  const completedCount = useMemo(() => {
    return sortedDeliveries.filter(
      (d) => d.status === "DELIVERED" || d.status === "FAILED"
    ).length;
  }, [sortedDeliveries]);

  const nextDeliveryId = useMemo(() => {
    const pending = sortedDeliveries.filter(
      (d) => d.status !== "DELIVERED" && d.status !== "FAILED"
    );
    const current = deliveryId ? pending.findIndex((d) => d.id === deliveryId) : -1;
    if (current >= 0 && current < pending.length - 1) {
      return pending[current + 1].id;
    }
    return null;
  }, [sortedDeliveries, deliveryId]);

  const addr = delivery?.order?.customerAddress;
  const customer = delivery?.order?.customer;
  /** Client réglé en compte par défaut → pas de mode de paiement à saisir à la livraison */
  const isCustomerOnAccount = customer?.defaultPaymentMode === "EN_COMPTE";

  const goToNext = useCallback(() => {
    if (nextDeliveryId) {
      navigate(`/deliveries/my/detail/${nextDeliveryId}`);
    } else {
      navigate("/deliveries/my");
    }
  }, [navigate, nextDeliveryId]);

  const handleConfirmDelivered = useCallback(() => {
    if (!deliveryId) return;
    const isLaterPayment = isCustomerOnAccount || paymentMethod === "PLUS_TARD";
    const notes = JSON.stringify({
      paymentMethod: isLaterPayment ? undefined : paymentMethod,
      paymentTiming: isLaterPayment ? "LATER" : "IMMEDIATE",
      comment: deliveredComment || undefined,
    });
    const now = getBusinessTimeHHmm();
    updateStatusMutation.mutate(
      { id: deliveryId, status: "DELIVERED", actualDeliveryTime: now, notes },
      {
        onSuccess: () => {
          setOpenDelivered(false);
          setDeliveredComment("");
          setPaymentMethod("CB");
          goToNext();
        },
      }
    );
  }, [deliveryId, isCustomerOnAccount, paymentMethod, deliveredComment, updateStatusMutation, goToNext]);

  const handleConfirmFailed = useCallback(() => {
    if (!deliveryId || !failureCause) return;
    const notes = JSON.stringify({
      failureCause,
      comment: failedComment || undefined,
    });
    updateStatusMutation.mutate(
      { id: deliveryId, status: "FAILED", notes },
      {
        onSuccess: () => {
          setOpenFailed(false);
          setFailureCause("");
          setFailedComment("");
          goToNext();
        },
      }
    );
  }, [deliveryId, failureCause, failedComment, updateStatusMutation, goToNext]);

  const mapUrl = getMapUrl(addr ?? null);
  const telUrl = toTelHref(customer?.phoneDialCode, customer?.phone);

  const order = delivery?.order;
  const settlementInfo = useMemo(() => {
    if (!delivery?.notes) return null;
    try {
      const parsed = JSON.parse(delivery.notes) as {
        paymentTiming?: "LATER" | "IMMEDIATE";
        paymentMethod?: string;
      };
      const paymentMethodLabel = parsed.paymentMethod
        ? ORDER_PAYMENT_SETTLEMENT_OPTIONS.find((o) => o.code === parsed.paymentMethod)?.label ??
          parsed.paymentMethod
        : undefined;
      return {
        paymentTiming: parsed.paymentTiming,
        paymentMethod: parsed.paymentMethod,
        paymentMethodLabel,
      };
    } catch {
      return null;
    }
  }, [delivery?.notes]);
  const hasDriverSettlementInfo = Boolean(
    settlementInfo?.paymentTiming || settlementInfo?.paymentMethod
  );
  const subtotalProducts =
    order?.items?.reduce(
      (sum, item) => sum + (Number(item.subtotal) ?? Number(item.unitPrice) * (item.quantity ?? 0)),
      0
    ) ?? 0;
  const deliveryCostNum =
    order?.serviceType === "CATERING" ? 0 : (Number(order?.deliveryCost) || 0);
  const discountNum = Number(order?.discountAmount) || 0;
  const total = Number(order?.totalAmount) ?? 0;
  const vatAmountStored = order?.vatAmount != null ? Number(order.vatAmount) : null;
  const vatRateStored = order?.vatRate != null ? Number(order.vatRate) : null;
  const vatAmount =
    vatAmountStored ??
    (total > 0 ? total - total / (1 + (vatRateStored ?? 0.085)) : 0);
  const vatRatePercent = (vatRateStored != null ? vatRateStored * 100 : 8.5).toFixed(1);

  if (loadingList || (deliveryId && loadingDetail)) {
    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center", minHeight: 200 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (!delivery && deliveryId) {
    return (
      <Box sx={{ p: 2 }}>
        <PageHeader title="Livraison" />
        <Typography color="text.secondary">Livraison introuvable.</Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/deliveries/my")} sx={{ mt: 2 }}>
          Retour
        </Button>
      </Box>
    );
  }

  const progressPercent =
    sortedDeliveries.length > 0
      ? Math.round((completedCount / sortedDeliveries.length) * 100)
      : 0;

  const isPending = delivery?.status === "PLANNED" || delivery?.status === "IN_PROGRESS";

  return (
    <Box sx={{ p: 2, maxWidth: 600, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/deliveries/my")}
          size="small"
        >
          Retour
        </Button>
      </Box>

      <PageHeader
        title={`Commande #${delivery?.order?.orderNumber ?? "—"}`}
        subtitle={delivery?.zone?.name ?? "—"}
      />

      {/* Progression de la tournée */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="overline" color="text.secondary" gutterBottom display="block">
          Progression de la tournée
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{ height: 8, borderRadius: 1, mb: 1 }}
          color="primary"
        />
        <Typography variant="body2" color="text.secondary">
          {completedCount}/{sortedDeliveries.length} terminée(s)
        </Typography>
      </Paper>

      {/* Client actuel */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography
          variant="overline"
          sx={{ color: "primary.main", fontWeight: 600 }}
          display="block"
          gutterBottom
        >
          Arrêt actuel
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
          <Box>
            <Typography variant="h3" sx={{ fontSize: "1.25rem", fontWeight: 700 }}>
              {customer ? formatCustomerName(customer) : "—"}
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 1, color: "text.secondary" }}>
              <LocationOn fontSize="small" />
              <Typography variant="body2">
                {addr
                  ? [addr.address, addr.postalCode, addr.city].filter(Boolean).join(", ")
                  : "—"}
              </Typography>
            </Stack>
          </Box>
          <Avatar sx={{ bgcolor: "primary.main" }}>
            {customer ? getCustomerInitials(customer) : "?"}
          </Avatar>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<MapIcon />}
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!mapUrl}
            fullWidth
          >
            Ouvrir la carte
          </Button>
          <Button
            variant="outlined"
            startIcon={<Phone />}
            href={telUrl}
            disabled={!telUrl}
            fullWidth
          >
            Appeler
          </Button>
        </Stack>
      </Paper>

      {/* Récapitulatif de la commande */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          <ReceiptLong sx={{ color: "primary.main", fontSize: 20 }} />
          <Typography variant="overline" sx={{ color: "primary.main", fontWeight: 600 }}>
            Récapitulatif de la commande
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            État de paiement client
          </Typography>
          <Chip
            size="small"
            label={order?.paymentStatus?.name ?? "Non renseigné"}
            sx={{
              bgcolor: order?.paymentStatus?.colorCode ?? "grey.200",
              color: "grey.900",
              fontWeight: 600,
            }}
          />
        </Box>
        {hasDriverSettlementInfo && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Règlement saisi par le livreur
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                size="small"
                label={
                  settlementInfo?.paymentTiming === "LATER"
                    ? "En compte (paiera plus tard)"
                    : "Comptant"
                }
                color={settlementInfo?.paymentTiming === "LATER" ? "warning" : "success"}
                variant="outlined"
              />
              {settlementInfo?.paymentMethodLabel && (
                <Chip
                  size="small"
                  label={settlementInfo.paymentMethodLabel}
                  variant="outlined"
                />
              )}
              {!settlementInfo?.paymentMethodLabel &&
                settlementInfo?.paymentTiming !== "LATER" &&
                order?.paymentMethod?.name && (
                  <Chip
                    size="small"
                    label={order.paymentMethod.name}
                    variant="outlined"
                  />
                )}
            </Stack>
          </Box>
        )}
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
          Détail des articles
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
          {(order?.items ?? []).map((item) => (
            <Box key={item.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Typography variant="body2">
                {item.quantity}× {item.product.name}
                {(item.notes || item.options) && (
                  <Typography component="span" variant="caption" color="text.secondary" display="block">
                    {item.notes || item.options}
                  </Typography>
                )}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {(Number(item.subtotal) ?? Number(item.unitPrice) * item.quantity).toFixed(2)} €
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Sous-total produits
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {subtotalProducts.toFixed(2)} €
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Livraison
              {order?.serviceType === "CATERING" && (
                <Typography component="span" variant="caption" sx={{ ml: 0.5, color: "text.secondary" }}>
                  (A emporter)
                </Typography>
              )}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {deliveryCostNum.toFixed(2)} €
            </Typography>
          </Box>
          {discountNum > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Réduction
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "success.main" }}>
                -{discountNum.toFixed(2)} €
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              TVA ({vatRatePercent} %)
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {vatAmount.toFixed(2)} €
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 2,
              mt: 1,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main" }}>
              Total à payer
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
              {total.toFixed(2)} €
            </Typography>
          </Box>
        </Box>
        {!!delivery?.customerOutstandingDue && delivery.customerOutstandingDue > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Montant total dû client :{" "}
              <strong>{delivery.customerOutstandingDue.toFixed(2)} €</strong>
            </Typography>
            {(delivery.customerOutstandingOrders ?? []).length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {delivery.customerOutstandingOrders!.map((line) => (
                  <Box component="li" key={line.id} sx={{ mb: 0.5 }}>
                    <Typography variant="body2">
                      #{line.orderNumber}
                      {line.isCurrent ? " (cette commande)" : ""} —{" "}
                      <strong>{line.totalAmount.toFixed(2)} €</strong>
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Alert>
        )}
      </Paper>

      {/* Actions */}
      {isPending && (
        <Stack direction="row" spacing={2}>
          <AppButton
            variant="primary"
            startIcon={<CheckCircle />}
            onClick={() => setOpenDelivered(true)}
            fullWidth
          >
            Livraison terminée
          </AppButton>
          <AppButton
            variant="outline"
            startIcon={<Cancel />}
            onClick={() => setOpenFailed(true)}
            fullWidth
            sx={{ borderColor: "error.main", color: "error.main" }}
          >
            Impossible de livrer
          </AppButton>
        </Stack>
      )}

      {!isPending && (
        <Chip
          label={STATUS_LABELS[delivery!.status]}
          color={delivery!.status === "DELIVERED" ? "success" : "error"}
          sx={{ mb: 2 }}
        />
      )}

      {/* Dialog Livraison terminée */}
      <Dialog open={openDelivered} onClose={() => !updateStatusMutation.isPending && setOpenDelivered(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Livraison terminée</DialogTitle>
        <DialogContent>
          {!isCustomerOnAccount ? (
            <FormControl component="fieldset" sx={{ mt: 2, display: "block" }}>
              <Typography variant="subtitle2" gutterBottom>
                Comment la course a été réglée ?
              </Typography>
              <RadioGroup
                value={paymentMethod}
                onChange={(e) =>
                  setPaymentMethod(e.target.value as OrderPaymentSettlementCode)
                }
                sx={{ gap: 0.5 }}
              >
                {ORDER_PAYMENT_SETTLEMENT_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.code}
                    value={opt.code}
                    control={<Radio size="small" />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              Ce client est en compte : aucun mode de paiement à saisir.
            </Alert>
          )}
          <TextField
            label="Commentaire (optionnel)"
            multiline
            rows={2}
            fullWidth
            value={deliveredComment}
            onChange={(e) => setDeliveredComment(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDelivered(false)} disabled={updateStatusMutation.isPending}>
            Annuler
          </Button>
          <AppButton
            variant="primary"
            onClick={handleConfirmDelivered}
            loading={updateStatusMutation.isPending}
          >
            Valider
          </AppButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Impossible de livrer */}
      <Dialog open={openFailed} onClose={() => !updateStatusMutation.isPending && setOpenFailed(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Impossible de livrer</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Cause</InputLabel>
            <Select
              value={failureCause}
              label="Cause"
              onChange={(e) => setFailureCause(e.target.value)}
            >
              {FAILURE_CAUSES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Commentaire (optionnel)"
            multiline
            rows={2}
            fullWidth
            value={failedComment}
            onChange={(e) => setFailedComment(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenFailed(false)} disabled={updateStatusMutation.isPending}>
            Annuler
          </Button>
          <AppButton
            variant="danger"
            onClick={handleConfirmFailed}
            loading={updateStatusMutation.isPending}
            disabled={!failureCause}
          >
            Valider
          </AppButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
